import { MutationCache, QueryClient, type Mutation } from '@tanstack/react-query';
import { toast } from '@/lib/toast';
import { ApiError, isStepUpRequiredError } from './api';
import { isErrorHandled } from './handledErrors';

/**
 * Per-mutation opt-out for the global error toast below. Register the shape so
 * `useMutation({ meta: { suppressErrorToast: true } })` type-checks.
 */
declare module '@tanstack/react-query' {
  interface Register {
    mutationMeta: {
      /** Skip the global error toast (the caller surfaces the error itself). */
      suppressErrorToast?: boolean;
    };
  }
}

/** The message to show for a failed mutation. */
export function mutationErrorMessage(error: unknown): string {
  if (error instanceof ApiError || error instanceof Error) {
    if (error.message) return error.message;
  }
  return 'Something went wrong. Please try again.';
}

/**
 * Should the global handler toast this failure? Not when the mutation handles
 * its own errors (`onError`), opted out via `meta.suppressErrorToast`, failed
 * with a 401 (the unauthorized listener already sends the admin to /login — a
 * toast per in-flight request would just pile up behind the redirect), or was
 * already shown inline by the caller (`markErrorHandled`, see handledErrors.ts).
 *
 * NB: ANY hook-level `onError` opts out — including one that only calls
 * `track()` for observability and shows nothing. Such a mutation's failure is
 * surfaced only by its caller (e.g. useLogin → the login form's inline error).
 */
export function shouldToastMutationError(
  error: unknown,
  mutation: Pick<Mutation<unknown, unknown, unknown, unknown>, 'options' | 'meta'>,
): boolean {
  if (mutation.options.onError) return false;
  if (mutation.meta?.suppressErrorToast) return false;
  // FIX(admin-stepup-server-enforced): a step-up refusal is a 401 that does NOT
  // redirect to /login, so it must still be surfaced ("Please confirm your
  // password again"). Before: every 401 was skipped. After: all but that one.
  if (error instanceof ApiError && error.status === 401 && !isStepUpRequiredError(error)) return false;
  if (isErrorHandled(error)) return false;
  return true;
}

/**
 * Shared Query client. Defaults tuned for an admin tool:
 *  - stale-while-revalidate (refetch on focus / reconnect)
 *  - no retry on 4xx (auth/permission/validation are terminal)
 *  - retry transient errors a couple times — this is the ONLY retry layer; the
 *    api client no longer retries on its own (FIX(admin-global-mutation-error))
 *  - every mutation failure is surfaced (see the MutationCache below)
 */
export const queryClient = new QueryClient({
  // FIX(admin-global-mutation-error): most mutations had no onError, so a
  // failed save/delete vanished silently. Before: no MutationCache handler.
  // After: one global toast carrying the API's message, skipped for mutations
  // that surface errors themselves (own onError / meta.suppressErrorToast).
  mutationCache: new MutationCache({
    onError: (error, _variables, _context, mutation) => {
      if (!shouldToastMutationError(error, mutation)) return;
      // FIX(admin-double-error): decide one macrotask later. This handler runs
      // BEFORE `mutateAsync` rejects, so a caller that catches and shows the
      // error inline (ConfirmDialog, dialogs with their own error line) has not
      // had the chance to `markErrorHandled` it yet. Before: toasted at once,
      // so those failures showed twice. After: the rejection's microtasks —
      // and so the caller's catch — run first; marked errors are skipped.
      setTimeout(() => {
        if (isErrorHandled(error)) return;
        toast.error('Action failed', { description: mutationErrorMessage(error) });
      }, 0);
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      retry: (failureCount, error) => {
        if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
        return failureCount < 2;
      },
    },
    mutations: {
      retry: false,
    },
  },
});
